import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Center, Paper, Stack, Text, Alert, Anchor, Loader } from '@mantine/core';
import { AlertCircle, CircleCheck } from 'lucide-react';
import { authenticatedFetch } from '@/shared/auth/AuthenticatedFetch';
import { UltraTraceWordmark } from '../../design/UltraTraceLogo';

type VerifyState = 'verifying' | 'done' | 'error';

/**
 * Public (pre-login) page that redeems a signed verify-email link on our own
 * branded surface — no Keycloak-hosted page. Runs the redemption once on mount.
 */
export function VerifyEmailPage(): React.ReactElement {
    const [params] = useSearchParams();
    const token = params.get('token') ?? '';
    const [state, setState] = useState<VerifyState>(token ? 'verifying' : 'error');
    const [message, setMessage] = useState('This link is invalid or has expired.');
    const ran = useRef(false);

    useEffect(() => {
        if (ran.current || !token) return;
        ran.current = true;
        void (async (): Promise<void> => {
            try {
                const res = await authenticatedFetch('/_auth/api/auth/verify-email', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ token }),
                });
                if (!res.ok) {
                    const body = (await res.json().catch(() => ({}))) as { error?: string };
                    setMessage(body.error ?? 'This link is invalid or has expired.');
                    setState('error');
                    return;
                }
                setState('done');
            } catch {
                setMessage('Something went wrong. Please try again.');
                setState('error');
            }
        })();
    }, [token]);

    return (
        <Center h="100dvh" p="md">
            <Paper shadow="md" p="xl" radius="md" withBorder w="100%" maw={420}>
                <Stack align="center">
                    <UltraTraceWordmark size={40} title="Ultra Trace" />
                    {state === 'verifying' && (
                        <Stack align="center" gap="sm">
                            <Loader />
                            <Text c="dimmed" size="sm">
                                Verifying your email…
                            </Text>
                        </Stack>
                    )}
                    {state === 'done' && (
                        <Stack align="center" gap="sm">
                            <CircleCheck size={40} color="var(--mantine-color-teal-6)" />
                            <Text ta="center">Your email has been verified. You can now sign in.</Text>
                            <Anchor href="/">Go to sign in</Anchor>
                        </Stack>
                    )}
                    {state === 'error' && (
                        <Alert variant="light" color="red" icon={<AlertCircle size={16} />} w="100%">
                            {message}
                        </Alert>
                    )}
                </Stack>
            </Paper>
        </Center>
    );
}

export default VerifyEmailPage;
